// frontend/src/pages/AllAssetPage.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../../services/api';
import ReactECharts from 'echarts-for-react';
import DateRangeControls from '../../components/DateRangeControls';
import Loading from '../../components/Loading';
import useDateRange from '../../hooks/useDateRange';
import { createChartOptions } from '../../utils/chartOptions';
import { Select, Typography, Card, Space, Alert, Divider, Tag } from 'antd';
import { LineChartOutlined, WarningOutlined } from '@ant-design/icons';

import ResponsiveTypography from '../../components/ResponsiveTypography';
const { Title } = ResponsiveTypography;
const {Text } = Typography;

// US Nominal GDP,US Real GDP,US CPI,US Core CPI,US M2

const ASSET_NAME_MAP = {
    'Gold': '금',
    'Silver' : '은',
    'Copper': '구리',

    'Bitcoin': '비트코인',
    'Ethereum': '이더리움',
    'S&P500': 'S&P500',
    'NASDAQ100': '나스닥100',
    'Russell2000': '러셀2000',
    'DowJones': '다우존스',
    'KOSPI': '코스피 종합',
    'KOSPI200': '코스피200',
    // 채권 자산 이름 추가
    'US01Y': '미국채 1년',
    'US02Y': '미국채 2년',
    'US05Y': '미국채 5년',
    'US10Y': '미국채 10년',
    'US30Y': '미국채 30년',
    'US-NominalGDP': '미국 명목 GDP',
    'US-RealGDP': '미국 실질 GDP',
    'US-CPI': '미국 CPI',
    'US-CoreCPI': '미국 근원 CPI',
    'US-M2': '미국 M2'
};

const global_stock_market_PERIODS = ['YTD', '3M', '6M', '1Y', '3Y', '5Y', '10Y', '15Y', '30Y', 'ALL'];

const AllAssetPage = () => {
    const [assetOptions, setAssetOptions] = useState([]);
    const [selectedAssets, setSelectedAssets] = useState(['S&P500', 'Gold', 'US10Y', 'US-M2']);
    const [priceData, setPriceData] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const [chartSeries, setChartSeries] = useState([]);
    const [assetWarnings, setAssetWarnings] = useState([]);
    const [assetColorMap, setAssetColorMap] = useState({});

    const { minDate, maxDate, viewStartDate, viewEndDate, handlePeriodChange, handleDateChange, isDateReady } = useDateRange(priceData, { dateColumn: 'Date', defaultPeriod: '5Y' });

    const chartRef = useRef(null);
    
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // 특정 컬럼만 지정하여 요청
                const stockColumns = ['Gold','Silver', 'Copper', 'S&P500',  'NASDAQ100', 'Bitcoin', 'Ethereum', 'Russell2000', 'DowJones',];
                const bondColumns = ['US01Y', 'US02Y', 'US05Y', 'US10Y', 'US30Y'];
                const usEconomicColumns = ['US-NominalGDP', 'US-CPI', 'US-CoreCPI', 'US-M2'];

                const [stockRes, bondRes, usEconomicRes] = await Promise.all([
                    apiClient.get('/global-stock-market/prices', {
                        params: { columns: stockColumns.join(',') }
                    }),
                    apiClient.get('/us-bond/cumulative-returns', {
                        params: { columns: bondColumns.join(',') }
                    }),
                    apiClient.get('/us-economic-data', {
                        params: { columns: usEconomicColumns.join(',') }
                    })
                ]);

                // 날짜를 기준으로 데이터 병합
                const mergedData = mergeDataByDate(stockRes.data, bondRes.data, usEconomicRes.data);
                setPriceData(mergedData);

                // 명시적으로 요청한 컬럼들로 assetOptions 설정
                const allColumns = [...stockColumns, ...bondColumns, ...usEconomicColumns];
                const options = allColumns.map(asset => ({ 
                    value: asset, 
                    label: ASSET_NAME_MAP[asset] || asset 
                }));
                setAssetOptions(options);
                
            } catch (error) {
                console.error("Failed to fetch chart data:", error);
                setPriceData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // 날짜를 기준으로 두 데이터 소스를 병합하는 함수
    const mergeDataByDate = (stockData, bondData, usEconomicData) => {
        const dataMap = new Map();

        const addToMap = (data) => {
            if (!data || !Array.isArray(data)) return; // 안전성 체크 추가
            data.forEach(item => {
                const date = item.Date;
                if (!dataMap.has(date)) {
                    dataMap.set(date, { Date: date });
                }
                Object.assign(dataMap.get(date), item);
            });
        };

        addToMap(stockData);
        addToMap(bondData);
        addToMap(usEconomicData); // 세 번째 데이터 소스 추가

        return Array.from(dataMap.values()).sort((a, b) => new Date(a.Date) - new Date(b.Date));
    };

    useEffect(() => {
        if (!isDateReady || !priceData.length) return;

        const dataToProcess = priceData;
        const newWarnings = [];
        const newSeries = selectedAssets.map(asset => {
            const firstDataPoint = dataToProcess.find(d => d[asset] != null && d[asset] > 0);
            const assetStartDate = firstDataPoint ? new Date(firstDataPoint.Date) : null;

            if (assetStartDate && viewStartDate < assetStartDate) {
                newWarnings.push(`'${ASSET_NAME_MAP[asset] || asset}' 데이터는 ${assetStartDate.toISOString().split('T')[0]}부터 제공됩니다.`);
            }

            let baseline = null;
            let baselineDate = null;
            const startIndex = dataToProcess.findIndex(d => new Date(d.Date) >= viewStartDate);

            if (startIndex !== -1) {
                for (let i = startIndex; i < dataToProcess.length; i++) {
                    if (dataToProcess[i][asset] != null && dataToProcess[i][asset] > 0) {
                        baseline = dataToProcess[i][asset];
                        baselineDate = new Date(dataToProcess[i].Date);
                        break;
                    }
                }
            }

            if (baseline === null) {
                return { name: ASSET_NAME_MAP[asset] || asset, type: 'line', data: [] };
            }

            const performanceData = dataToProcess.map(d => {
                const currentDate = new Date(d.Date);
                if (currentDate < baselineDate || d[asset] == null || d[asset] <= 0) {
                    return [d.Date, null];
                }
                const performance = ((d[asset] / baseline) - 1) * 100;
                return [d.Date, performance.toFixed(1)];
            });

            return { name: ASSET_NAME_MAP[asset] || asset, type: 'line', data: performanceData, showSymbol: false };
        });
        
        setAssetWarnings([...new Set(newWarnings)]);
        setChartSeries(newSeries);
    }, [isDateReady, priceData, selectedAssets, viewStartDate]);

    // chartSeries가 변경될 때 차트 색상과 Select 태그 색상을 동기화합니다.
    useEffect(() => {
        // chartRef에 인스턴스가 있고, chartSeries 데이터가 있을 때만 실행
        if (chartRef.current && chartSeries.length > 0) {
            const echartsInstance = chartRef.current.getEchartsInstance();
            const colors = echartsInstance.getOption().color;
            const newColorMap = {};

            selectedAssets.forEach((asset, index) => {
                newColorMap[asset] = colors[index % colors.length];
            });

            // 기존 색상 맵과 다를 때만 상태를 업데이트하여 무한 루프를 방지합니다.
            if (JSON.stringify(newColorMap) !== JSON.stringify(assetColorMap)) {
                setAssetColorMap(newColorMap);
            }
        }
    }, [chartSeries, selectedAssets, assetColorMap]);
    
    const handleCustomPeriodChange = (period) => {
        if (period === 'YTD') {
            const today = maxDate ? new Date(maxDate) : new Date();
            const previousYear = today.getFullYear() - 1;
            const newStartDate = new Date(previousYear, 11, 31);
            
            handleDateChange('start', newStartDate);
            handleDateChange('end', today);
        } else {
            handlePeriodChange(period);
        }
    };

    const tagRender = (props) => {
        const { label, value, closable, onClose } = props;
        const color = assetColorMap[value] || '#1677ff';

        return (
            <Tag
                color={color}
                closable={closable}
                onClose={onClose}
                style={{ marginRight: 3, color: 'white', fontWeight: '500' }}
            >
                {label}
            </Tag>
        );
    };

    const getChartOption = useCallback(() => {
        return createChartOptions({
            xAxis: { type: 'time'},
            yAxis: { type: 'value', scale: true, axisLabel: { formatter: '{value}%' } , position: 'right', axisLine: { show: true } },
            dataZoom: [
                { type: 'slider', show: false, xAxisIndex: [0], bottom: '3%', startValue: viewStartDate?.toISOString(), endValue: viewEndDate?.toISOString() },
                { type: 'inside', xAxisIndex: [0] }
            ],
            grid: {
                top: '100rem',
                bottom: '20rem',
                left: '20rem',
                right: '20rem',
                containLabel: true,
            },
            series: chartSeries,
            tooltip: { 
                trigger: 'axis', 
                axisPointer: {type: 'cross' },
                valueFormatter: (value) => value != null ? `${value}%` : 'N/A' 
            },
            legend: { data: selectedAssets.map(asset => ASSET_NAME_MAP[asset] || asset), top: '5%' },
        });
    }, [viewStartDate, viewEndDate, chartSeries, selectedAssets]);

    const onChartEvents = {
        'datazoom': () => {
            if (chartRef.current) {
                const chartInstance = chartRef.current.getEchartsInstance();
                const newOption = chartInstance.getOption();
                
                if (newOption && newOption.dataZoom && newOption.dataZoom.length > 0) {
                    const newStartValue = newOption.dataZoom[0].startValue;
                    const newEndValue = newOption.dataZoom[0].endValue;

                    if (newStartValue != null && newEndValue != null) {
                        const newStartDate = new Date(newStartValue);
                        const newEndDate = new Date(newEndValue);

                        if (viewStartDate && viewEndDate && (newStartDate.getTime() !== viewStartDate.getTime() || newEndDate.getTime() !== viewEndDate.getTime())) {
                            handleDateChange('start', newStartDate);
                            handleDateChange('end', newEndDate);
                        }
                    }
                }
            }
        },
    };

    if (loading || !isDateReady) {
        return (
            <Loading />
        );
    }

    return (
        <div className="content-box">
            <Title style={{ marginLeft: "0.625rem", marginRight: "0.625rem", marginBottom: '2.0rem', marginTop: '1.5rem' }} level={2}>
                글로벌 자산 성과 비교
            </Title>

            <Card style={{ marginTop: '0.625rem' }} bodyStyle={{ padding: '1.0rem' }}>
                 <Space direction="vertical" style={{ width: '100%'}}>
                    <Text strong style={{ fontSize: '1rem' }}>
                        <LineChartOutlined /> 비교할 자산을 선택하세요
                    </Text>
                    <Select
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        placeholder="자산을 선택하세요"
                        value={selectedAssets}
                        onChange={setSelectedAssets}
                        options={assetOptions}
                        optionFilterProp="label"
                        tagRender={tagRender}
                    />
                </Space>
            </Card>

            {assetWarnings.length > 0 && (
                <Alert
                    message={<strong>데이터 기간 안내</strong>}
                    description={
                        <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                            {assetWarnings.map((warning, index) => (
                                <li key={index}>{warning}</li>
                            ))}
                        </ul>
                    }
                    type="warning"
                    showIcon
                    icon={<WarningOutlined />}
                    style={{ marginTop: '1.0rem' }}
                />
            )}
            
            <Card style={{ marginTop: '1.0rem', padding: '0.625rem' }} bodyStyle={{ padding: '0rem' }}>
                <ReactECharts 
                    ref={chartRef}
                    option={getChartOption()} 
                    style={{ height: '31.25rem' }} 
                    showLoading={loading}
                    onEvents={onChartEvents}
                />
                <Divider style={{ marginTop: '0.625rem', marginBottom: '0.625rem' }} />
                <DateRangeControls
                   minDate={minDate}
                   maxDate={maxDate}
                   viewStartDate={viewStartDate}
                   viewEndDate={viewEndDate}
                   onPeriodChange={handleCustomPeriodChange}
                   onDateChange={handleDateChange}
                   periods={global_stock_market_PERIODS}
               />
            </Card>
        </div>
    );
};

export default AllAssetPage;